import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormLabel,
  IconButton,
  Select,
  Wrap,
  WrapItem,
  Checkbox,
  Spinner,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import InsertRole from "./insertRole";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";

export const RoleList = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [editableRole, setEditableRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const pageId = 18;
  const [pageOptions, setPageOptions] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  // Fetch page options from API
  useEffect(() => {
    const fetchPages = async () => {
      setLoadingPages(true);
      try {
        const token = sessionStorage.getItem("accessToken");
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/pages?page_id=${pageId}`,
          "GET",
          null,
          { Authorization: `Bearer ${token}` }
        );
        if (response && response.status === "success" && Array.isArray(response.data)) {
          setPageOptions(response.data);
        } else {
          toast({
            title: t("Failed to fetch pages"),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      } catch (err) {
        toast({
          title: t("Failed to fetch pages"),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setLoadingPages(false);
      }
    };
    fetchPages();
  }, [t]);

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }
      const permission = role.permissions.find((p) => p.page_id === 18);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetchWithToken(`${process.env.REACT_APP_API_URL}/roles?page_id=${pageId}`);
        if (!response) {
          throw new Error("Failed to fetch roles");
        }
        setRoles(response.data);
        setFilteredRoles(response.data);
        toast({
          title: "Roles fetched successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } catch (error) {
        toast({
          title: "An error occurred.",
          description: error.message || "Failed to fetch roles.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchRoles();
  }, []);

  const handleDeleteRole = async (roleId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/roles/delete?page_id=${pageId}`,
        "DELETE",
        { role_id: roleId }
      );
      if (!response) {
        throw new Error("Failed to delete role");
      }
      setRoles((prevRoles) => prevRoles.filter((role) => role.role_id !== roleId));
      setFilteredRoles((prevFilteredRoles) => prevFilteredRoles.filter((role) => role.role_id !== roleId));
      toast({
        title: "Role deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to delete role.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleEditRole = (role) => {
    setEditableRole(role);
  };

  const handleSaveRole = async () => {
    try {
      setIsSubmitting(true);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/roles/update?page_id=${pageId}`,
        "PATCH",
        editableRole
      );
      if (!response) {
        throw new Error("Failed to update role");
      }
      setRoles((prevRoles) =>
        prevRoles.map((role) => (role.role_id === editableRole.role_id ? editableRole : role))
      );
      setFilteredRoles((prevFilteredRoles) =>
        prevFilteredRoles.map((role) => (role.role_id === editableRole.role_id ? editableRole : role))
      );
      setEditableRole(null);
      toast({
        title: "Role updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to update role.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = roles.filter((role) =>
      role.role_name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredRoles(filtered);
  };

  const handlePermissionChange = (index, field, value) => {
    const updatedPermissions = [...editableRole.permissions];
    updatedPermissions[index][field] = value;
    setEditableRole({ ...editableRole, permissions: updatedPermissions })
  };

  const removePermission = (index) => {
    const updatedPermissions = editableRole.permissions.filter((_, i) => i !== index);
    setEditableRole({ ...editableRole, permissions: updatedPermissions })
  };

  const addPermission = () => {
    const updatedPermissions = [...editableRole.permissions,
      {
        page_id: "",
        page_access: false,
        read_all_access: false,
        read_one_access: false,
        insert_access: false,
        update_access: false,
        delete_access: false,
        export_access: false,
      },];
    setEditableRole({ ...editableRole, permissions: updatedPermissions });
  };

  const handleSelectAllForPermission = (index, value) => {
    const updatedPermissions = editableRole.permissions.map((perm, i) =>
      i === index
        ? {
          ...perm,
          page_access: value,
          read_all_access: value,
          read_one_access: value,
          insert_access: value,
          update_access: value,
          delete_access: value,
          export_access: value,
        }
        : perm
    );
    setEditableRole({ ...editableRole, permissions: updatedPermissions });
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Role List")}
          </Text>
          <Button
            onClick={() => setShowCreateRoleModal(true)}
            mb={1}
            isDisabled={!permissions.canInsert}
            ml={4}
            color="white"
            colorScheme="cyan"
            borderRadius="full"
            boxShadow="sm"
            _hover={{ bg: "green.600", transform: "scale(1.05)" }}
            _active={{ bg: "green.700", transform: "scale(0.95)" }}
            transition="all 0.2s"
          >
            <MdAdd />
          </Button>
        </Flex>
        <Input placeholder={t("Search role")} value={searchQuery} onChange={handleSearch} size="sm" w={{ base: "100%", md: "200px" }} />
      </Flex>

      {isMobile ? (
        /** MOBILE VIEW - Render as Cards **/
        <Box overflowY={"auto"} maxH={"70vh"} px="10px">
          <SimpleGrid columns={{ base: 1 }} spacing={4} px="10px">
            {filteredRoles.map((role) => (
              <Card key={role.role_id} p="4" borderRadius="md" shadow="md">
                <Box mb="3">
                  <Text fontSize="lg" fontWeight="bold">
                    {role.role_name}
                  </Text>
                  <Badge colorScheme="purple">{role.role_category}</Badge>
                </Box>
                <Box>
                  <Text fontSize="sm">
                    <b>{t("Permissions")}: </b>
                    {role.permissions.length}
                  </Text>
                  <Flex mt="2">
                    <Button colorScheme="cyan" size="xs" mr="2" isDisabled={!permissions.canEdit} onClick={() => handleEditRole(role)}>
                      <MdEdit color="white" />
                    </Button>
                    {role.role_id > 5 ? (
                      <Button
                        colorScheme="red"
                        size="xs"
                        isDisabled={!permissions.canDelete}
                        mr="2"
                        onClick={() => handleDeleteRole(role.role_id)}
                      >
                        <MdDelete />
                      </Button>
                    ) : (
                      <Button
                        colorScheme="red"
                        size="xs"
                        mr="2"
                        disabled
                      >
                        <MdDelete />
                      </Button>
                    )}
                  </Flex>
                </Box>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      ) : (
        /** DESKTOP VIEW - Render as Table **/
        <Box overflowX="auto" overflowY={"auto"} maxH={"60vh"} px="0px">
          <Table variant="simple">
            <Thead>
              <Tr bg='rgb(69, 50, 240)' textColor="white">
                <Th textColor="white">{t("Role Name")}</Th>
                <Th textColor="white">{t("Role Category")}</Th>
                <Th textColor="white">{t("Permissions")}</Th>
                <Th textColor="white">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRoles.map((role) => (
                <Tr key={role.role_id}>
                  <Td>{role.role_name}</Td>
                  <Td>
                    <Badge colorScheme="purple">{role.role_category}</Badge>
                  </Td>
                  <Td>{role.permissions.length}</Td>
                  <Td>
                    <Flex>
                      <Button colorScheme="cyan" size="sm" mr="2" onClick={() => handleEditRole(role)}>
                        <MdEdit color="white" />
                      </Button>
                      {role.role_id > 5 ? (
                        <Button
                          colorScheme="red"
                          size="sm"
                          mr="2"
                          onClick={() => handleDeleteRole(role.role_id)}
                        >
                          <MdDelete />
                        </Button>
                      ) : (
                        <Button
                          colorScheme="red"
                          size="sm"
                          mr="2"
                          disabled
                        >
                          <MdDelete />
                        </Button>
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {editableRole && (
        <Modal isOpen={true} onClose={() => setEditableRole(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                {t("Edit Role")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {editableRole.role_id <= 5 ? (
                // Only allow editing the role name and permissions for roles 1 to 5
                <>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb="2">
                      {t("Role Name")}
                    </Text>
                    <Input
                      value={editableRole.role_name}
                      onChange={(e) => setEditableRole({ ...editableRole, role_name: e.target.value })}
                    />
                  </Box>
                  <Box mt="6">
                    <Text fontSize="lg" fontWeight="bold" mb="2">
                      {t("Permissions")}
                    </Text>
                    {/* Accordion for Permissions */}
                    <Accordion allowToggle>
                      {editableRole.permissions.map((perm, index) => (
                        <AccordionItem key={index} borderWidth="1px" borderRadius="lg" mb={4}>
                          <h2>
                            <AccordionButton _expanded={{ bg: "gray.100" }}>
                              <Box flex="1" textAlign="left">
                                {t("Page")}: {pageOptions.find(p => p.id === parseInt(perm.page_id))?.name || t("Select a Page")}
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={4}>
                            <Box>
                              {/* Page Selection */}
                              <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                                {t("Page")}
                              </FormLabel>
                              <Select
                                variant="filled"
                                value={perm.page_id}
                                onChange={(e) =>
                                  handlePermissionChange(index, "page_id", e.target.value)
                                }
                                required
                                isDisabled={loadingPages}
                              >
                                <option value="">{loadingPages ? t("Loading...") : t("Select a Page")}</option>
                                {pageOptions.map((page) => (
                                  <option key={page.id} value={page.id}>
                                    {t(page.name)}  ({t(page.category)})
                                  </option>
                                ))}
                              </Select>
                            </Box>
                            {/* Select All / Deselect All for Individual Permissions */}
                            <Flex mt={4} gap={2}>
                              <Button
                                colorScheme="teal"
                                size="xs"
                                onClick={() =>
                                  handleSelectAllForPermission(index, true)
                                }
                              >
                                {t("Select All")}
                              </Button>
                              <Button
                                colorScheme="gray"
                                size="xs"
                                onClick={() =>
                                  handleSelectAllForPermission(index, false)
                                }
                              >
                                {t("Deselect All")}
                              </Button>
                            </Flex>
                            {/* Permissions Checkboxes */}
                            <Box mt={4}>
                              <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                                {t("Access Rights")}
                              </FormLabel>
                              <Wrap spacing={4}>
                                {["page_access", "read_all_access", "read_one_access", "insert_access", "update_access", "delete_access", "export_access"].map((field) => (
                                  <WrapItem key={field}>
                                    <Checkbox
                                      colorScheme="blue"
                                      isChecked={perm[field]}
                                      onChange={(e) =>
                                        handlePermissionChange(index, field, e.target.checked)
                                      }
                                    >
                                      <Text fontSize="sm">
                                        {t(field.replace(/_/g, " ").toUpperCase())}
                                      </Text>
                                    </Checkbox>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            </Box>
                            {/* Delete Button */}
                            <Flex justify="flex-end" mt={4}>
                              <IconButton
                                aria-label="Remove permission"
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="outline"
                                size="sm"
                                onClick={() => removePermission(index)}
                              />
                            </Flex>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    {/* Add Permission Button */}
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="blue"
                      size="sm"
                      mt={4}
                      onClick={addPermission}
                    >
                      {t("Add Permission")}
                    </Button>
                  </Box>
                </>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb="2">
                      {t("Role Name")}
                    </Text>
                    <Input
                      value={editableRole.role_name}
                      onChange={(e) => setEditableRole({ ...editableRole, role_name: e.target.value })}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb="2">
                      {t("Role Category")}
                    </Text>
                    <Input
                      value={editableRole.role_category}
                      onChange={(e) => setEditableRole({ ...editableRole, role_category: e.target.value })}
                    />
                  </Box>
                </SimpleGrid>
              )}
              {editableRole.role_id > 5 && (
                // Permissions section shown only when role id is greater than 5
                <Box mt="6">
                  <Text fontSize="lg" fontWeight="bold" mb="2">
                    {t("Permissions")}
                  </Text>
                  {/* Accordion for Permissions */}
                  <Accordion allowToggle>
                    {editableRole.permissions.map((perm, index) => (
                      <AccordionItem key={index} borderWidth="1px" borderRadius="lg" mb={4}>
                        <h2>
                          <AccordionButton _expanded={{ bg: "gray.100" }}>
                            <Box flex="1" textAlign="left">
                              {t("Page")}: {pageOptions.find(p => p.id === parseInt(perm.page_id))?.name || t("Select a Page")}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Box>
                            {/* Page Selection */}
                            <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                              {t("Page")}
                            </FormLabel>
                            <Select
                              variant="filled"
                              value={perm.page_id}
                              onChange={(e) =>
                                handlePermissionChange(index, "page_id", e.target.value)
                              }
                              required
                              isDisabled={loadingPages}
                            >
                              <option value="">{loadingPages ? t("Loading...") : t("Select a Page")}</option>
                              {pageOptions.map((page) => (
                                <option key={page.id} value={page.id}>
                                  {t(page.name)}  ({t(page.category)})
                                </option>
                              ))}
                            </Select>
                          </Box>
                          {/* Select All / Deselect All for Individual Permissions */}
                          <Flex mt={4} gap={2}>
                            <Button
                              colorScheme="teal"
                              size="xs"
                              onClick={() =>
                                handleSelectAllForPermission(index, true)
                              }
                            >
                              {t("Select All")}
                            </Button>
                            <Button
                              colorScheme="gray"
                              size="xs"
                              onClick={() =>
                                handleSelectAllForPermission(index, false)
                              }
                            >
                              {t("Deselect All")}
                            </Button>
                          </Flex>
                          {/* Permissions Checkboxes */}
                          <Box mt={4}>
                            <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                              {t("Access Rights")}
                            </FormLabel>
                            <Wrap spacing={4}>
                              {["page_access", "read_all_access", "read_one_access", "insert_access", "update_access", "delete_access", "export_access"].map((field) => (
                                <WrapItem key={field}>
                                  <Checkbox
                                    colorScheme="blue"
                                    isChecked={perm[field]}
                                    onChange={(e) =>
                                      handlePermissionChange(index, field, e.target.checked)
                                    }
                                  >
                                    <Text fontSize="sm">
                                      {t(field.replace(/_/g, " ").toUpperCase())}
                                    </Text>
                                  </Checkbox>
                                </WrapItem>
                              ))}
                            </Wrap>
                          </Box>
                          {/* Delete Button */}
                          <Flex justify="flex-end" mt={4}>
                            <IconButton
                              aria-label="Remove permission"
                              icon={<DeleteIcon />}
                              colorScheme="red"
                              variant="outline"
                              size="sm"
                              onClick={() => removePermission(index)}
                            />
                          </Flex>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {/* Add Permission Button */}
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    size="sm"
                    mt={4}
                    onClick={addPermission}
                  >
                    {t("Add Permission")}
                  </Button>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="cyan" onClick={handleSaveRole} color="white" isLoading={isSubmitting} borderRadius="full" boxShadow="md">
                {t("Save")}
              </Button>
              <Button colorScheme="red" ml={3} onClick={() => setEditableRole(null)} borderRadius="full" boxShadow="md">
                {t("Cancel")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateRoleModal} onClose={() => setShowCreateRoleModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent borderRadius="lg" boxShadow="xl" bg={useColorModeValue("white", "gray.800")}>
          <ModalHeader>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              {t("Create Role")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InsertRole />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default RoleList;